import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ScaleSheetHeightResizeHandle } from "@/components/scale-sheet/scale-sheet-height-resize-handle"
import { ScaleSheetPreview } from "@/components/scale-sheet/scale-sheet-preview"
import {
  SCALE_SHEET_EDIT_FRAME_PADDING_Y_PX,
  scaleSheetFigureEntityKey,
  type ScaleSheetPlacedFigure,
} from "@/lib/scale-sheet-layout"

const HOVER_LEAVE_DELAY_MS = 200

type Props = {
  previewFigures: ScaleSheetPlacedFigure[]
  contentWidth: number
  serverImageUrl?: string | null
  showServerImage: boolean
  serverImageOpenUrl?: string | null
  previewLoading: boolean
  showBusyOverlay?: boolean
  previewDisplayZone?: { top: number; bottom: number }
  heightCmForEntity: (entityKey: string) => number
  onHeightChange: (entityKey: string, cm: number) => void
  alwaysActiveEntityKey?: string
  fixedFrameHeightPx?: number
  missingFigurePlaceholder?: "character" | "prop"
  minHeightCm?: number
  minDragHeightCm?: number
  minHeightCmForEntity?: (entityKey: string) => number
  minDragHeightCmForEntity?: (entityKey: string) => number
  heightLabel?: string
  onRegenerateFigure?: (entityKey: string) => void
  regenerateFigureTitle?: string | ((entityKey: string) => string)
  isFigureBusy?: (entityKey: string) => boolean
  figureGenerateDisabled?: (entityKey: string) => { disabled: boolean; reason?: string }
}

export function ScaleSheetEditPanel({
  previewFigures,
  contentWidth,
  serverImageUrl,
  showServerImage,
  serverImageOpenUrl,
  previewLoading,
  showBusyOverlay = true,
  previewDisplayZone,
  heightCmForEntity,
  onHeightChange,
  alwaysActiveEntityKey,
  fixedFrameHeightPx,
  missingFigurePlaceholder = "character",
  minHeightCm,
  minDragHeightCm,
  minHeightCmForEntity,
  minDragHeightCmForEntity,
  heightLabel,
  onRegenerateFigure,
  regenerateFigureTitle,
  isFigureBusy,
  figureGenerateDisabled,
}: Props) {
  const [hoveredEntityKey, setHoveredEntityKey] = useState<string | null>(null)
  const [pinnedEntityKey, setPinnedEntityKey] = useState<string | null>(null)
  const [resizeActive, setResizeActive] = useState(false)
  const hoverLeaveTimerRef = useRef<number | null>(null)
  const activeEntityKeyRef = useRef<string | null>(null)

  const activeEntityKey = useMemo(
    () => alwaysActiveEntityKey ?? pinnedEntityKey ?? hoveredEntityKey,
    [alwaysActiveEntityKey, pinnedEntityKey, hoveredEntityKey],
  )

  useEffect(() => {
    activeEntityKeyRef.current = activeEntityKey
  }, [activeEntityKey])

  const showResizeHandle =
    !showServerImage &&
    !previewLoading &&
    activeEntityKey != null &&
    previewFigures.some((f) => scaleSheetFigureEntityKey(f) === activeEntityKey)

  useEffect(() => {
    if (!showResizeHandle) setResizeActive(false)
  }, [showResizeHandle])

  useEffect(() => {
    return () => {
      if (hoverLeaveTimerRef.current != null) {
        window.clearTimeout(hoverLeaveTimerRef.current)
      }
    }
  }, [])

  const cancelHoverLeave = useCallback(() => {
    if (hoverLeaveTimerRef.current != null) {
      window.clearTimeout(hoverLeaveTimerRef.current)
      hoverLeaveTimerRef.current = null
    }
  }, [])

  const onHoverEntity = useCallback(
    (entityKey: string) => {
      if (alwaysActiveEntityKey != null) return
      cancelHoverLeave()
      setHoveredEntityKey(entityKey)
    },
    [alwaysActiveEntityKey, cancelHoverLeave],
  )

  const onUnhoverEntity = useCallback(() => {
    if (alwaysActiveEntityKey != null || pinnedEntityKey != null) return
    cancelHoverLeave()
    hoverLeaveTimerRef.current = window.setTimeout(() => {
      setHoveredEntityKey(null)
      hoverLeaveTimerRef.current = null
    }, HOVER_LEAVE_DELAY_MS)
  }, [alwaysActiveEntityKey, pinnedEntityKey, cancelHoverLeave])

  const onTapEntity = useCallback(
    (entityKey: string) => {
      if (alwaysActiveEntityKey != null) return
      cancelHoverLeave()
      setHoveredEntityKey(entityKey)
    },
    [alwaysActiveEntityKey, cancelHoverLeave],
  )

  const onTapBackground = useCallback(() => {
    if (alwaysActiveEntityKey != null || pinnedEntityKey != null) return
    cancelHoverLeave()
    setHoveredEntityKey(null)
  }, [alwaysActiveEntityKey, pinnedEntityKey, cancelHoverLeave])

  const onDragChange = useCallback((dragging: boolean) => {
    if (dragging) {
      const key = activeEntityKeyRef.current
      if (key != null) setPinnedEntityKey(key)
    } else {
      setPinnedEntityKey(null)
    }
  }, [])

  const activeMinHeightCm =
    activeEntityKey != null ? (minHeightCmForEntity?.(activeEntityKey) ?? minHeightCm) : minHeightCm
  const activeMinDragHeightCm =
    activeEntityKey != null ? (minDragHeightCmForEntity?.(activeEntityKey) ?? minDragHeightCm) : minDragHeightCm

  const canvasOverlay =
    showResizeHandle && activeEntityKey != null ? (
      <ScaleSheetHeightResizeHandle
        key={activeEntityKey}
        heightCm={heightCmForEntity(activeEntityKey)}
        onHeightChange={(cm) => onHeightChange(activeEntityKey, cm)}
        onActiveChange={setResizeActive}
        onDragChange={onDragChange}
        minHeightCm={activeMinHeightCm}
        minDragHeightCm={activeMinDragHeightCm}
        heightLabel={heightLabel}
      />
    ) : null

  return (
    <ScaleSheetPreview
      figures={previewFigures}
      contentWidth={contentWidth}
      activeEntityKey={activeEntityKey}
      onHoverEntity={alwaysActiveEntityKey == null ? onHoverEntity : undefined}
      onUnhoverEntity={alwaysActiveEntityKey == null ? onUnhoverEntity : undefined}
      onTapEntity={alwaysActiveEntityKey == null ? onTapEntity : undefined}
      onTapBackground={alwaysActiveEntityKey == null ? onTapBackground : undefined}
      serverImageUrl={serverImageUrl}
      showServerImage={showServerImage}
      compactServerImage={showServerImage}
      serverImageOpenUrl={serverImageOpenUrl}
      loading={previewLoading}
      showBusyOverlay={showBusyOverlay}
      compactPreviewHeight
      previewDisplayZone={previewDisplayZone}
      placeholderSurface="canvas"
      missingFigurePlaceholder={missingFigurePlaceholder}
      canvasOverlay={canvasOverlay}
      selectionResizeActive={resizeActive}
      framePaddingY={SCALE_SHEET_EDIT_FRAME_PADDING_Y_PX}
      fixedFrameHeightPx={fixedFrameHeightPx}
      onRegenerateFigure={onRegenerateFigure}
      regenerateFigureTitle={regenerateFigureTitle}
      isFigureBusy={isFigureBusy}
      figureGenerateDisabled={figureGenerateDisabled}
    />
  )
}
